import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, SafeAreaView, StatusBar,
  Alert, RefreshControl, TextInput, Modal, Dimensions, ActivityIndicator,
  Platform, KeyboardAvoidingView, FlatList,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');
const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P = '#0e4e96';
const SEC = '#1e7be2';
const SUCCESS = '#10b981';
const WARNING = '#f59e0b';
const DANGER = '#ef4444';
const GRAY = '#6B7280';

const getH = async () => {
  const t = await AsyncStorage.getItem('auth_token').catch(() => null) || await AsyncStorage.getItem('token').catch(() => null);
  return { 'Content-Type': 'application/json', 'x-api-key': KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};

const getCompanyId = async () => {
  try {
    const ud = await AsyncStorage.getItem('currentUser') || await AsyncStorage.getItem('user_data');
    if (ud) { const u = JSON.parse(ud); return u.company_id || u.companyId || null; }
    const cp = await AsyncStorage.getItem('companyProfile') || await AsyncStorage.getItem('company_profile');
    if (cp) { const c = JSON.parse(cp); return c.id || c.company_id || null; }
    return await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id') || '1';
  } catch { return '0'; }
};

const CATEGORIES = [
  { key: 'travel', label: 'Travel', icon: 'airplane' },
  { key: 'meals', label: 'Meals', icon: 'food' },
  { key: 'accommodation', label: 'Accommodation', icon: 'bed' },
  { key: 'equipment', label: 'Equipment', icon: 'laptop' },
  { key: 'training', label: 'Training', icon: 'school' },
  { key: 'communication', label: 'Communication', icon: 'phone' },
  { key: 'transport', label: 'Transport', icon: 'car' },
  { key: 'office_supplies', label: 'Office Supplies', icon: 'pencil' },
  { key: 'software', label: 'Software', icon: 'application' },
  { key: 'entertainment', label: 'Entertainment', icon: 'glass-cocktail' },
  { key: 'medical', label: 'Medical', icon: 'medical-bag' },
  { key: 'other', label: 'Other', icon: 'dots-horizontal' },
];

const STATUS_COLORS = { pending: WARNING, approved: SUCCESS, rejected: DANGER, paid: P, draft: GRAY, processing: SEC, reimbursed: SUCCESS };

const TABS = [
  { k: 'overview', t: 'Overview', i: 'view-dashboard' },
  { k: 'expenses', t: 'Expenses', i: 'receipt' },
  { k: 'reports', t: 'Reports', i: 'file-chart' },
  { k: 'budgets', t: 'Budgets', i: 'chart-pie' },
  { k: 'policies', t: 'Policies', i: 'shield-check' },
  { k: 'analytics', t: 'Analytics', i: 'chart-bar' },
];

export default function ExpenseReportScreen({ navigation, route }) {
  const empId = route?.params?.employeeId || '1';
  const [activeTab, setActiveTab] = useState('overview');
  const [expenses, setExpenses] = useState([]);
  const [reports, setReports] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create'); // create, edit, detail, report
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [companyId, setCompanyId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    category: 'travel', amount: '', currency: 'GBP', description: '',
    date: new Date().toISOString().split('T')[0], receipt_url: '',
    vendor_name: '', project: '', notes: '', mileage: '', tax_amount: '',
  });

  const [stats, setStats] = useState({
    totalClaimed: 0, totalApproved: 0, totalPending: 0, totalRejected: 0,
    totalReimbursed: 0, claimCount: 0, avgClaim: 0, topCategory: '-',
    monthlySpend: 0, budgetRemaining: 0,
  });

  // ===== Init =====
  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      setLoading(true);
      const cid = await getCompanyId();
      setCompanyId(cid);
      await fetchAll(cid);
    } catch (e) { console.error('Init error:', e); }
    finally { setLoading(false); }
  };

  const onRefresh = async () => { setRefreshing(true); await init(); setRefreshing(false); };

  // ===== Fetch All Data =====
  const fetchAll = async (cid) => {
    const h = await getH();
    const cParam = cid ? `&company_id=${cid}` : '';

    const fetchData = async (endpoint, cacheKey) => {
      try {
        const res = await fetch(`${API}/api/data/${endpoint}?employee_id=${parseInt(empId) || 1}${cParam}&limit=500`, { headers: h });
        const json = await res.json().catch(() => ({}));
        const data = Array.isArray(json?.data) ? json.data : [];
        if (data.length > 0) await AsyncStorage.setItem(cacheKey, JSON.stringify(data));
        return data;
      } catch (e) {
        console.warn(`Fetch ${endpoint}:`, e.message);
        try { const c = await AsyncStorage.getItem(cacheKey); return c ? JSON.parse(c) : []; } catch { return []; }
      }
    };

    const [expData, repData, budData, polData] = await Promise.all([
      fetchData('expense_reports', `expenses_${empId}`),
      fetchData('expense_report_summaries', `expense_reports_${empId}`),
      fetchData('expense_budgets', `expense_budgets_${empId}`),
      fetchData('expense_policies', `expense_policies_${cid}`),
    ]);

    setExpenses(expData);
    setReports(repData);
    setBudgets(budData);
    setPolicies(polData);

    // Calculate stats
    const total = expData.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const approved = expData.filter(e => e.status === 'approved').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const pending = expData.filter(e => e.status === 'pending').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const rejected = expData.filter(e => e.status === 'rejected').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const reimbursed = expData.filter(e => e.status === 'reimbursed' || e.status === 'paid').reduce((s, e) => s + parseFloat(e.amount || 0), 0);

    // Top category
    const catCounts = {};
    expData.forEach(e => { const c = e.category || 'other'; catCounts[c] = (catCounts[c] || 0) + parseFloat(e.amount || 0); });
    const topCat = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0];

    // Monthly spend (current month)
    const now = new Date();
    const monthlyData = expData.filter(e => { const d = new Date(e.expense_date || e.created_at); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); });
    const monthlySpend = monthlyData.reduce((s, e) => s + parseFloat(e.amount || 0), 0);

    const budgetTotal = budData.reduce((s, b) => s + parseFloat(b.total_budget || b.amount || 0), 0);
    const budgetUsed = budData.reduce((s, b) => s + parseFloat(b.spent || b.used_amount || 0), 0);

    setStats({
      totalClaimed: total, totalApproved: approved, totalPending: pending,
      totalRejected: rejected, totalReimbursed: reimbursed,
      claimCount: expData.length, avgClaim: expData.length > 0 ? total / expData.length : 0,
      topCategory: topCat ? topCat[0] : '-', monthlySpend,
      budgetRemaining: budgetTotal - budgetUsed,
    });
  };

  // ===== CRUD =====
  const submitExpense = async () => {
    if (!form.amount || isNaN(parseFloat(form.amount))) { Alert.alert('Error', 'Please enter a valid amount'); return; }
    if (!form.description.trim()) { Alert.alert('Error', 'Please add a description'); return; }
    setSaving(true);
    try {
      const h = await getH();
      const expense = {
        employee_id: parseInt(empId) || 1,
        company_id: parseInt(companyId) || 0,
        category: form.category,
        amount: parseFloat(form.amount),
        currency: form.currency,
        description: form.description,
        expense_date: form.date,
        vendor_name: form.vendor_name,
        project: form.project,
        notes: form.notes,
        mileage: form.mileage ? parseFloat(form.mileage) : null,
        tax_amount: form.tax_amount ? parseFloat(form.tax_amount) : null,
        receipt_url: form.receipt_url,
        status: 'pending',
        created_at: new Date().toISOString(),
      };

      let newItem = expense;
      try {
        const res = await fetch(`${API}/api/data/expense_reports`, { method: 'POST', headers: h, body: JSON.stringify(expense) });
        const json = await res.json().catch(() => ({}));
        if (json?.data) newItem = { ...expense, id: json.data.id || json.data[0]?.id };
      } catch (e) {
        console.warn('API create failed:', e.message);
        newItem = { ...expense, id: `local_${Date.now()}` };
      }

      const updated = [newItem, ...expenses];
      setExpenses(updated);
      await AsyncStorage.setItem(`expenses_${empId}`, JSON.stringify(updated));
      Alert.alert('✅ Expense Submitted', `£${parseFloat(form.amount).toFixed(2)} ${form.category} expense submitted`);
      setShowModal(false);
      resetForm();
      init();
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setSaving(false); }
  };

  const updateExpense = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      const h = await getH();
      const payload = { ...form, amount: parseFloat(form.amount) || 0, company_id: parseInt(companyId) || 0, updated_at: new Date().toISOString() };
      try {
        await fetch(`${API}/api/data/expense_reports/${selected.id}`, { method: 'PUT', headers: h, body: JSON.stringify(payload) });
      } catch (e) { console.warn('API update failed:', e.message); }

      const updated = expenses.map(ex => String(ex.id) === String(selected.id) ? { ...ex, ...payload } : ex);
      setExpenses(updated);
      await AsyncStorage.setItem(`expenses_${empId}`, JSON.stringify(updated));
      Alert.alert('✅ Updated', 'Expense updated successfully');
      setShowModal(false);
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setSaving(false); }
  };

  const deleteExpense = (item) => {
    Alert.alert('Delete Expense', `Delete this £${parseFloat(item.amount || 0).toFixed(2)} expense?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try {
          const h = await getH();
          await fetch(`${API}/api/data/expense_reports/${item.id}`, { method: 'DELETE', headers: h }).catch(() => {});
        } catch {}
        const updated = expenses.filter(e => String(e.id) !== String(item.id));
        setExpenses(updated);
        await AsyncStorage.setItem(`expenses_${empId}`, JSON.stringify(updated));
        setShowModal(false); setSelected(null);
      }},
    ]);
  };

  const duplicateExpense = (item) => {
    setForm({
      category: item.category || 'travel', amount: String(item.amount || ''),
      currency: item.currency || 'GBP', description: item.description || '',
      date: new Date().toISOString().split('T')[0], receipt_url: '',
      vendor_name: item.vendor_name || '', project: item.project || '',
      notes: '', mileage: item.mileage ? String(item.mileage) : '', tax_amount: '',
    });
    setModalType('create');
    setShowModal(true);
  };

  const submitForApproval = async (item) => {
    try {
      const h = await getH();
      await fetch(`${API}/api/data/expense_reports/${item.id}`, { method: 'PUT', headers: h, body: JSON.stringify({ status: 'pending', submitted_at: new Date().toISOString() }) }).catch(() => {});
      const updated = expenses.map(e => String(e.id) === String(item.id) ? { ...e, status: 'pending', submitted_at: new Date().toISOString() } : e);
      setExpenses(updated);
      await AsyncStorage.setItem(`expenses_${empId}`, JSON.stringify(updated));
      Alert.alert('✅ Submitted', 'Expense submitted for approval');
    } catch (e) { console.warn('Submit failed:', e.message); }
  };

  // ===== Helpers =====
  const formatCurrency = (v) => `£${parseFloat(v || 0).toFixed(2)}`;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  const getCategoryIcon = (cat) => CATEGORIES.find(c => c.key === (cat || '').toLowerCase())?.icon || 'receipt-text';
  const getCategoryLabel = (cat) => CATEGORIES.find(c => c.key === (cat || '').toLowerCase())?.label || cat || 'Other';
  const getStatusColor = (status) => STATUS_COLORS[(status || '').toLowerCase()] || GRAY;

  const resetForm = () => setForm({
    category: 'travel', amount: '', currency: 'GBP', description: '',
    date: new Date().toISOString().split('T')[0], receipt_url: '',
    vendor_name: '', project: '', notes: '', mileage: '', tax_amount: '',
  });

  // ===== Filtered Data =====
  const filteredExpenses = useMemo(() => {
    let data = expenses;
    if (filterStatus !== 'all') data = data.filter(e => (e.status || '').toLowerCase() === filterStatus);
    if (filterCategory !== 'all') data = data.filter(e => (e.category || '').toLowerCase() === filterCategory);
    if (searchQuery) { const q = searchQuery.toLowerCase(); data = data.filter(e => JSON.stringify(e).toLowerCase().includes(q)); }
    return data;
  }, [expenses, filterStatus, filterCategory, searchQuery]);

  // ===== Render Header =====
  const renderHeader = () => (
    <LinearGradient colors={[P, SEC]} style={s.header}>
      <View style={s.headerRow}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={s.hBtn}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={s.headerTitle}>Expense Reports</Text>
          <Text style={s.headerSub}>Company ID: {companyId || '-'} · Employee: {empId}</Text>
        </View>
        <TouchableOpacity onPress={() => { resetForm(); setModalType('create'); setShowModal(true); }} style={s.hBtn}><MaterialCommunityIcons name="plus" size={22} color="#fff" /></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  // ===== Render Overview =====
  const renderOverview = () => {
    const dashStats = [
      { label: 'Total Claimed', value: formatCurrency(stats.totalClaimed), icon: 'cash-multiple', color: P },
      { label: 'Approved', value: formatCurrency(stats.totalApproved), icon: 'check-circle', color: SUCCESS },
      { label: 'Pending', value: formatCurrency(stats.totalPending), icon: 'clock-outline', color: WARNING },
      { label: 'Rejected', value: formatCurrency(stats.totalRejected), icon: 'close-circle', color: DANGER },
      { label: 'Reimbursed', value: formatCurrency(stats.totalReimbursed), icon: 'bank-transfer', color: SUCCESS },
      { label: 'Total Claims', value: stats.claimCount, icon: 'file-document-multiple', color: SEC },
      { label: 'Avg Claim', value: formatCurrency(stats.avgClaim), icon: 'calculator', color: P },
      { label: 'Monthly Spend', value: formatCurrency(stats.monthlySpend), icon: 'calendar-month', color: WARNING },
      { label: 'Top Category', value: getCategoryLabel(stats.topCategory), icon: getCategoryIcon(stats.topCategory), color: SEC },
      { label: 'Budget Left', value: formatCurrency(stats.budgetRemaining), icon: 'chart-pie', color: stats.budgetRemaining > 0 ? SUCCESS : DANGER },
    ];

    return (
      <View>
        <View style={s.section}><Text style={s.secTitle}>Dashboard</Text>
          <View style={s.grid}>
            {dashStats.map((st2, i) => (
              <View key={i} style={s.gridCard}>
                <MaterialCommunityIcons name={st2.icon} size={22} color={st2.color} />
                <Text style={[s.gridVal, { color: st2.color }]}>{st2.value}</Text>
                <Text style={s.gridLbl}>{st2.label}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Category Breakdown */}
        <View style={s.section}><Text style={s.secTitle}>Spend by Category</Text>
          {(() => {
            const cats = {};
            expenses.forEach(e => { const c = getCategoryLabel(e.category); cats[c] = (cats[c] || 0) + parseFloat(e.amount || 0); });
            const maxVal = Math.max(...Object.values(cats), 1);
            return Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, amount], i) => (
              <View key={i} style={s.barRow}>
                <Text style={s.barLbl} numberOfLines={1}>{name}</Text>
                <View style={s.barBg}><View style={[s.barFill, { width: `${(amount / maxVal) * 100}%` }]} /></View>
                <Text style={[s.barCnt, { width: 65 }]}>{formatCurrency(amount)}</Text>
              </View>
            ));
          })()}
          {expenses.length === 0 && <Text style={s.emptyText}>No expenses yet</Text>}
        </View>

        {/* Recent Expenses */}
        <View style={s.section}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text style={s.secTitle}>Recent Expenses</Text>
            <TouchableOpacity onPress={() => setActiveTab('expenses')}><Text style={{ color: P, fontWeight: '600', fontSize: 13 }}>View All</Text></TouchableOpacity>
          </View>
          {expenses.slice(0, 5).map((e, i) => renderExpenseCard(e, i))}
          {expenses.length === 0 && <View style={s.emptyBox}><MaterialCommunityIcons name="receipt" size={48} color="#cbd5e1" /><Text style={s.emptyText}>No expenses yet</Text>
            <TouchableOpacity style={s.emptyBtn} onPress={() => { resetForm(); setModalType('create'); setShowModal(true); }}><Text style={s.emptyBtnText}>Submit Expense</Text></TouchableOpacity></View>}
        </View>

        {/* Monthly Trend */}
        <View style={s.section}><Text style={s.secTitle}>Monthly Trend</Text>
          {(() => {
            const months = {};
            expenses.forEach(e => { const d = new Date(e.expense_date || e.created_at); const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; months[key] = (months[key] || 0) + parseFloat(e.amount || 0); });
            const maxM = Math.max(...Object.values(months), 1);
            return Object.entries(months).sort().slice(-6).map(([m, a], i) => (
              <View key={i} style={s.barRow}><Text style={s.barLbl}>{m}</Text><View style={s.barBg}><View style={[s.barFill, { width: `${(a / maxM) * 100}%`, backgroundColor: SEC }]} /></View><Text style={[s.barCnt, { width: 65 }]}>{formatCurrency(a)}</Text></View>
            ));
          })()}
        </View>
      </View>
    );
  };

  // ===== Render Expense Card =====
  const renderExpenseCard = (e, i) => (
    <TouchableOpacity key={e.id || i} style={s.expenseCard} onPress={() => { setSelected(e); setModalType('detail'); setShowModal(true); }}>
      <View style={[s.catIcon, { backgroundColor: P + '15' }]}>
        <MaterialCommunityIcons name={getCategoryIcon(e.category)} size={20} color={P} />
      </View>
      <View style={{ flex: 1, marginLeft: 12 }}>
        <Text style={s.expenseDesc} numberOfLines={1}>{e.description || getCategoryLabel(e.category)}</Text>
        <Text style={s.expenseDate}>{getCategoryLabel(e.category)} · {formatDate(e.expense_date || e.created_at)}{e.vendor_name ? ` · ${e.vendor_name}` : ''}</Text>
      </View>
      <View style={{ alignItems: 'flex-end' }}>
        <Text style={s.expenseAmount}>{formatCurrency(e.amount)}</Text>
        <View style={[s.statusBadge, { backgroundColor: getStatusColor(e.status) + '20' }]}>
          <Text style={[s.statusText, { color: getStatusColor(e.status) }]}>{(e.status || 'pending').toUpperCase()}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  // ===== Render Expenses List =====
  const renderExpensesList = () => (
    <View>
      {/* Search */}
      <View style={s.searchRow}><MaterialCommunityIcons name="magnify" size={20} color="#9CA3AF" />
        <TextInput style={s.searchInput} placeholder="Search expenses..." placeholderTextColor="#9CA3AF" value={searchQuery} onChangeText={setSearchQuery} />
        {searchQuery ? <TouchableOpacity onPress={() => setSearchQuery('')}><MaterialCommunityIcons name="close-circle" size={18} color="#9CA3AF" /></TouchableOpacity> : null}
      </View>
      {/* Status Filter */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingHorizontal: 16, marginBottom: 4 }}>
        {['all', 'pending', 'approved', 'rejected', 'paid', 'draft'].map((st2, i) => (
          <TouchableOpacity key={i} style={[s.fChip, filterStatus === st2 && { backgroundColor: P + '20' }]} onPress={() => setFilterStatus(st2)}>
            <Text style={[s.fChipTxt, filterStatus === st2 && { color: P, fontWeight: '600' }]}>{st2 === 'all' ? 'All' : st2.charAt(0).toUpperCase() + st2.slice(1)}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      {/* Category Filter */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingHorizontal: 16, marginBottom: 8 }}>
        <TouchableOpacity style={[s.fChip, filterCategory === 'all' && { backgroundColor: P + '20' }]} onPress={() => setFilterCategory('all')}>
          <Text style={[s.fChipTxt, filterCategory === 'all' && { color: P, fontWeight: '600' }]}>All Categories</Text>
        </TouchableOpacity>
        {CATEGORIES.map((c, i) => (
          <TouchableOpacity key={i} style={[s.fChip, filterCategory === c.key && { backgroundColor: P + '20' }]} onPress={() => setFilterCategory(c.key)}>
            <MaterialCommunityIcons name={c.icon} size={14} color={filterCategory === c.key ? P : GRAY} />
            <Text style={[s.fChipTxt, { marginLeft: 4 }, filterCategory === c.key && { color: P, fontWeight: '600' }]}>{c.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      {/* List */}
      <View style={{ paddingHorizontal: 16 }}>
        {filteredExpenses.length === 0 ? (
          <View style={s.emptyBox}><MaterialCommunityIcons name="receipt" size={48} color="#cbd5e1" /><Text style={s.emptyText}>No expenses match filters</Text></View>
        ) : filteredExpenses.map((e, i) => renderExpenseCard(e, i))}
      </View>
    </View>
  );

  // ===== Render Analytics =====
  const renderAnalytics = () => {
    const byStatus = {};
    expenses.forEach(e => { const st2 = e.status || 'pending'; byStatus[st2] = (byStatus[st2] || 0) + 1; });
    const byCat = {};
    expenses.forEach(e => { const c = getCategoryLabel(e.category); byCat[c] = (byCat[c] || 0) + parseFloat(e.amount || 0); });
    const byMonth = {};
    expenses.forEach(e => { const d = new Date(e.expense_date || e.created_at); const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; byMonth[key] = (byMonth[key] || 0) + parseFloat(e.amount || 0); });

    return (
      <View>
        <View style={s.section}><Text style={s.secTitle}>Status Distribution</Text><View style={s.grid}>
          {Object.entries(byStatus).map(([st2, c], i) => (
            <View key={i} style={s.gridCard}><Text style={[s.gridVal, { color: getStatusColor(st2) }]}>{c}</Text><Text style={s.gridLbl}>{st2.charAt(0).toUpperCase() + st2.slice(1)}</Text></View>
          ))}</View></View>

        <View style={s.section}><Text style={s.secTitle}>Category Spending</Text>
          {(() => { const max = Math.max(...Object.values(byCat), 1);
            return Object.entries(byCat).sort((a, b) => b[1] - a[1]).map(([n, a], i) => (
              <View key={i} style={s.barRow}><Text style={s.barLbl} numberOfLines={1}>{n}</Text><View style={s.barBg}><View style={[s.barFill, { width: `${(a / max) * 100}%` }]} /></View><Text style={[s.barCnt, { width: 65 }]}>{formatCurrency(a)}</Text></View>
            )); })()}</View>

        <View style={s.section}><Text style={s.secTitle}>Monthly Spending</Text>
          {(() => { const max = Math.max(...Object.values(byMonth), 1);
            return Object.entries(byMonth).sort().slice(-12).map(([m, a], i) => (
              <View key={i} style={s.barRow}><Text style={s.barLbl}>{m}</Text><View style={s.barBg}><View style={[s.barFill, { width: `${(a / max) * 100}%`, backgroundColor: SEC }]} /></View><Text style={[s.barCnt, { width: 65 }]}>{formatCurrency(a)}</Text></View>
            )); })()}</View>

        <View style={s.section}><Text style={s.secTitle}>All Metrics</Text><View style={s.grid}>
          {Object.entries(stats).map(([k, v], i) => (
            <View key={i} style={s.gridCard}><Text style={[s.gridVal, { color: P }]}>{typeof v === 'number' ? (v > 100 ? formatCurrency(v) : v % 1 !== 0 ? v.toFixed(1) : String(v)) : String(v)}</Text>
              <Text style={s.gridLbl}>{k.replace(/([A-Z])/g, ' $1').replace(/^./, ch => ch.toUpperCase())}</Text></View>
          ))}</View></View>
      </View>
    );
  };

  // ===== Render Budgets =====
  const renderBudgets = () => (
    <View style={s.section}><Text style={s.secTitle}>Expense Budgets</Text>
      {budgets.length === 0 ? <Text style={s.emptyText}>No budgets configured</Text> :
        budgets.map((b, i) => {
          const total = parseFloat(b.total_budget || b.amount || 0);
          const spent = parseFloat(b.spent || b.used_amount || 0);
          const pct = total > 0 ? Math.min(Math.round((spent / total) * 100), 100) : 0;
          return (
            <View key={i} style={{ marginBottom: 16, padding: 12, backgroundColor: '#F9FAFB', borderRadius: 10 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                <Text style={{ fontWeight: '600', color: '#1e293b' }}>{b.name || b.category || `Budget ${i + 1}`}</Text>
                <Text style={{ fontWeight: '700', color: pct > 90 ? DANGER : pct > 70 ? WARNING : SUCCESS }}>{pct}%</Text>
              </View>
              <View style={s.barBg}><View style={[s.barFill, { width: `${pct}%`, backgroundColor: pct > 90 ? DANGER : pct > 70 ? WARNING : SUCCESS, height: 8 }]} /></View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                <Text style={{ fontSize: 12, color: GRAY }}>Spent: {formatCurrency(spent)}</Text>
                <Text style={{ fontSize: 12, color: GRAY }}>Total: {formatCurrency(total)}</Text>
              </View>
            </View>
          );
        })}
    </View>
  );

  // ===== Render Policies =====
  const renderPolicies = () => (
    <View style={s.section}><Text style={s.secTitle}>Expense Policies</Text>
      {policies.length === 0 ? (
        <View>
          <Text style={s.emptyText}>No custom policies configured. Default limits apply:</Text>
          {[
            { cat: 'Travel', limit: '£500/trip', note: 'Requires pre-approval over £200' },
            { cat: 'Meals', limit: '£50/day', note: 'Business meals only' },
            { cat: 'Accommodation', limit: '£150/night', note: 'Must book through approved channels' },
            { cat: 'Equipment', limit: '£1,000', note: 'Requires manager approval' },
            { cat: 'Entertainment', limit: '£100/event', note: 'Client entertainment only' },
          ].map((p, i) => (
            <View key={i} style={{ padding: 12, backgroundColor: '#F9FAFB', borderRadius: 10, marginBottom: 8 }}>
              <Text style={{ fontWeight: '600', color: '#1e293b' }}>{p.cat}</Text>
              <Text style={{ fontSize: 13, color: SUCCESS, fontWeight: '700', marginTop: 2 }}>Limit: {p.limit}</Text>
              <Text style={{ fontSize: 12, color: GRAY, marginTop: 2 }}>{p.note}</Text>
            </View>
          ))}
        </View>
      ) : policies.map((p, i) => (
        <View key={i} style={{ padding: 12, backgroundColor: '#F9FAFB', borderRadius: 10, marginBottom: 8 }}>
          <Text style={{ fontWeight: '600', color: '#1e293b' }}>{p.name || p.category}</Text>
          <Text style={{ fontSize: 13, color: SUCCESS, fontWeight: '700', marginTop: 2 }}>Limit: {formatCurrency(p.limit_amount)}</Text>
          {p.description && <Text style={{ fontSize: 12, color: GRAY, marginTop: 2 }}>{p.description}</Text>}
        </View>
      ))}
    </View>
  );

  // ===== Render Reports =====
  const renderReports = () => (
    <View style={s.section}><Text style={s.secTitle}>Expense Reports</Text>
      {reports.length === 0 ? <Text style={s.emptyText}>No reports generated yet</Text> :
        reports.map((r, i) => (
          <TouchableOpacity key={i} style={s.expenseCard} onPress={() => { setSelected(r); setModalType('detail'); setShowModal(true); }}>
            <View style={[s.catIcon, { backgroundColor: SEC + '15' }]}><MaterialCommunityIcons name="file-chart" size={20} color={SEC} /></View>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={s.expenseDesc}>{r.title || r.name || `Report #${r.id}`}</Text>
              <Text style={s.expenseDate}>{formatDate(r.created_at)} · {r.expense_count || 0} expenses</Text>
            </View>
            <Text style={s.expenseAmount}>{formatCurrency(r.total_amount || r.total)}</Text>
          </TouchableOpacity>
        ))}
    </View>
  );

  // ===== Render Tabs =====
  const renderTabs = () => (
    <View style={s.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 12 }}>
      {TABS.map((tab, i) => (
        <TouchableOpacity key={i} style={[s.tab, activeTab === tab.k && s.activeTab]} onPress={() => { setActiveTab(tab.k); setSearchQuery(''); setFilterStatus('all'); setFilterCategory('all'); }}>
          <MaterialCommunityIcons name={tab.i} size={18} color={activeTab === tab.k ? P : GRAY} />
          <Text style={[s.tabTxt, activeTab === tab.k && { color: P, fontWeight: '600' }]}>{tab.t}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView></View>
  );

  // ===== Stats Bar =====
  const renderStatsBar = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.statsBar} contentContainerStyle={{ paddingHorizontal: 16 }}>
      {[
        { l: 'Claimed', v: formatCurrency(stats.totalClaimed) },
        { l: 'Pending', v: formatCurrency(stats.totalPending) },
        { l: 'Approved', v: formatCurrency(stats.totalApproved) },
        { l: 'Claims', v: stats.claimCount },
      ].map((st2, i) => (
        <View key={i} style={s.statCard}><Text style={[s.statVal, { color: P }]}>{st2.v}</Text><Text style={s.statLbl}>{st2.l}</Text></View>
      ))}
    </ScrollView>
  );

  // ===== Render Content =====
  const renderContent = () => {
    const wrap = (ch) => (<ScrollView style={{ flex: 1 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[P]} />} showsVerticalScrollIndicator={false}>{ch}<View style={{ height: 40 }} /></ScrollView>);
    switch (activeTab) {
      case 'overview': return wrap(renderOverview());
      case 'expenses': return wrap(renderExpensesList());
      case 'reports': return wrap(renderReports());
      case 'budgets': return wrap(renderBudgets());
      case 'policies': return wrap(renderPolicies());
      case 'analytics': return wrap(renderAnalytics());
      default: return wrap(renderOverview());
    }
  };

  // ===== Render Form =====
  const renderForm = () => (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
      <ScrollView style={{ padding: 16 }}>
        <Text style={s.fieldLabel}>Category</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 16 }}>
          {CATEGORIES.map(c => (
            <TouchableOpacity key={c.key} onPress={() => setForm(p => ({ ...p, category: c.key }))}
              style={[s.chip, form.category === c.key && s.chipActive]}>
              <MaterialCommunityIcons name={c.icon} size={14} color={form.category === c.key ? '#fff' : GRAY} />
              <Text style={[s.chipText, { marginLeft: 4 }, form.category === c.key && { color: '#fff' }]}>{c.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        <Text style={s.fieldLabel}>Amount (£) *</Text>
        <TextInput style={s.input} value={form.amount} onChangeText={v => setForm(p => ({ ...p, amount: v }))} placeholder="0.00" keyboardType="decimal-pad" />

        <Text style={s.fieldLabel}>Description *</Text>
        <TextInput style={s.input} value={form.description} onChangeText={v => setForm(p => ({ ...p, description: v }))} placeholder="Brief description of expense..." />

        <Text style={s.fieldLabel}>Date</Text>
        <TextInput style={s.input} value={form.date} onChangeText={v => setForm(p => ({ ...p, date: v }))} placeholder="YYYY-MM-DD" />

        <Text style={s.fieldLabel}>Vendor / Supplier</Text>
        <TextInput style={s.input} value={form.vendor_name} onChangeText={v => setForm(p => ({ ...p, vendor_name: v }))} placeholder="Company or person name" />

        <Text style={s.fieldLabel}>Project / Department</Text>
        <TextInput style={s.input} value={form.project} onChangeText={v => setForm(p => ({ ...p, project: v }))} placeholder="Link to project or department" />

        <Text style={s.fieldLabel}>Tax Amount (£)</Text>
        <TextInput style={s.input} value={form.tax_amount} onChangeText={v => setForm(p => ({ ...p, tax_amount: v }))} placeholder="0.00" keyboardType="decimal-pad" />

        <Text style={s.fieldLabel}>Mileage (miles)</Text>
        <TextInput style={s.input} value={form.mileage} onChangeText={v => setForm(p => ({ ...p, mileage: v }))} placeholder="0" keyboardType="decimal-pad" />

        <Text style={s.fieldLabel}>Notes</Text>
        <TextInput style={[s.input, { height: 80, textAlignVertical: 'top' }]} value={form.notes} onChangeText={v => setForm(p => ({ ...p, notes: v }))} placeholder="Additional notes..." multiline />

        <TouchableOpacity style={s.submitBtn} onPress={modalType === 'edit' ? updateExpense : submitExpense} disabled={saving}>
          <MaterialCommunityIcons name={modalType === 'edit' ? 'content-save' : 'send'} size={20} color="#fff" />
          <Text style={s.submitBtnTxt}>{saving ? 'Saving...' : modalType === 'edit' ? 'Save Changes' : 'Submit Expense'}</Text>
        </TouchableOpacity>

        {modalType === 'edit' && selected?.id && (
          <TouchableOpacity style={[s.submitBtn, { backgroundColor: DANGER, marginTop: 12 }]} onPress={() => deleteExpense(selected)}>
            <MaterialCommunityIcons name="delete" size={20} color="#fff" />
            <Text style={s.submitBtnTxt}>Delete Expense</Text>
          </TouchableOpacity>
        )}
        <View style={{ height: 40 }} />
      </ScrollView>
    </KeyboardAvoidingView>
  );

  // ===== Render Detail =====
  const renderDetail = () => {
    if (!selected) return null;
    return (
      <ScrollView style={{ flex: 1, padding: 16 }}>
        <View style={s.detailSection}>
          <Text style={s.detailTitle}>Expense Details</Text>
          {Object.entries(selected).filter(([k, v]) => typeof v !== 'object' && v != null && !['id', 'company_id', 'employee_id'].includes(k)).slice(0, 20).map(([k, v], i) => (
            <View key={i} style={s.detailRow}>
              <Text style={s.detailKey}>{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Text>
              <Text style={s.detailVal}>{k.includes('amount') || k.includes('total') || k === 'amount' ? formatCurrency(v) : k.includes('date') || k.includes('_at') ? formatDate(v) : String(v).substring(0, 120)}</Text>
            </View>
          ))}
        </View>

        <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
          <TouchableOpacity style={[s.submitBtn, { flex: 1, backgroundColor: P }]} onPress={() => { setForm({ ...selected, amount: String(selected.amount || '') }); setModalType('edit'); }}>
            <MaterialCommunityIcons name="pencil" size={18} color="#fff" /><Text style={s.submitBtnTxt}>Edit</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.submitBtn, { flex: 1, backgroundColor: SEC }]} onPress={() => { duplicateExpense(selected); }}>
            <MaterialCommunityIcons name="content-copy" size={18} color="#fff" /><Text style={s.submitBtnTxt}>Duplicate</Text>
          </TouchableOpacity>
        </View>
        {selected.status === 'draft' && (
          <TouchableOpacity style={[s.submitBtn, { backgroundColor: SUCCESS, marginTop: 8 }]} onPress={() => { submitForApproval(selected); setShowModal(false); }}>
            <MaterialCommunityIcons name="send" size={18} color="#fff" /><Text style={s.submitBtnTxt}>Submit for Approval</Text>
          </TouchableOpacity>
        )}
        <View style={{ height: 40 }} />
      </ScrollView>
    );
  };

  // ===== Modal =====
  const renderModal = () => (
    <Modal visible={showModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
        <View style={s.modalHeader}>
          <TouchableOpacity onPress={() => { setShowModal(false); resetForm(); }}><MaterialCommunityIcons name="close" size={22} color="#1e293b" /></TouchableOpacity>
          <Text style={s.modalTitle}>{modalType === 'create' ? 'New Expense' : modalType === 'edit' ? 'Edit Expense' : selected?.description || 'Details'}</Text>
          <View style={{ width: 22 }} />
        </View>
        {modalType === 'detail' ? renderDetail() : renderForm()}
      </SafeAreaView>
    </Modal>
  );

  // ===== Loading =====
  if (loading) return (<SafeAreaView style={s.safe}><StatusBar barStyle="dark-content" /><View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}><ActivityIndicator size="large" color={P} /><Text style={{ color: GRAY, marginTop: 12 }}>Loading expenses...</Text></View></SafeAreaView>);

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      {renderHeader()}
      {renderStatsBar()}
      {renderTabs()}
      {renderContent()}
      {renderModal()}
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { paddingHorizontal: 16, paddingVertical: 14 }, headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' }, headerSub: { color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 },
  statsBar: { flexGrow: 0, backgroundColor: '#fff', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  statCard: { alignItems: 'center', marginRight: 16, minWidth: 70 }, statVal: { fontSize: 15, fontWeight: 'bold' }, statLbl: { fontSize: 10, color: '#6B7280', marginTop: 2 },
  tabsRow: { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }, tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 10, marginRight: 4, borderRadius: 20 }, activeTab: { backgroundColor: '#EFF6FF' }, tabTxt: { fontSize: 12, color: '#6B7280', marginLeft: 5, fontWeight: '500' },
  section: { backgroundColor: '#fff', margin: 16, marginBottom: 0, padding: 16, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB' }, secTitle: { fontSize: 16, fontWeight: 'bold', color: '#1e293b', marginBottom: 12 },
  grid: { flexDirection: 'row', flexWrap: 'wrap' }, gridCard: { width: Dimensions.get('window').width / 2 - 32, backgroundColor: '#F9FAFB', borderRadius: 10, padding: 14, alignItems: 'center', margin: 4 }, gridVal: { fontSize: 16, fontWeight: 'bold', marginTop: 4 }, gridLbl: { fontSize: 11, color: '#6B7280', marginTop: 4, textAlign: 'center' },
  expenseCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 8, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, elevation: 1, borderWidth: 1, borderColor: '#F3F4F6' },
  catIcon: { width: 44, height: 44, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  expenseDesc: { fontSize: 14, fontWeight: '600', color: '#1e293b' }, expenseDate: { fontSize: 11, color: '#64748b', marginTop: 2 }, expenseAmount: { fontSize: 16, fontWeight: '900', color: '#1e293b' },
  statusBadge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 5, marginTop: 4 }, statusText: { fontSize: 9, fontWeight: '800' },
  searchRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8, margin: 16, marginBottom: 8 }, searchInput: { flex: 1, fontSize: 14, color: '#1F2937', marginLeft: 8 },
  fChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#F3F4F6', marginRight: 6 }, fChipTxt: { fontSize: 12, color: '#6B7280', fontWeight: '500' },
  barRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 }, barLbl: { fontSize: 12, color: '#1e293b', width: 90 }, barBg: { flex: 1, height: 6, backgroundColor: '#E5E7EB', borderRadius: 3, marginHorizontal: 8, overflow: 'hidden' }, barFill: { height: '100%', backgroundColor: '#0e4e96', borderRadius: 3 }, barCnt: { fontSize: 12, fontWeight: '600', color: '#1e293b', width: 30, textAlign: 'right' },
  emptyBox: { alignItems: 'center', padding: 32 }, emptyText: { color: '#94a3b8', fontSize: 14, marginTop: 8 }, emptyBtn: { marginTop: 16, backgroundColor: '#0e4e96', paddingHorizontal: 24, paddingVertical: 10, borderRadius: 12 }, emptyBtnText: { color: '#fff', fontWeight: '700' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' }, modalTitle: { fontSize: 16, fontWeight: '700', color: '#1e293b' },
  fieldLabel: { fontSize: 12, fontWeight: '700', color: '#374151', marginBottom: 6 },
  input: { backgroundColor: '#fff', borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0', padding: 12, marginBottom: 16, fontSize: 14, color: '#1e293b' },
  chip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, borderWidth: 1.5, borderColor: '#e2e8f0', marginRight: 8, backgroundColor: '#fff' }, chipActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' }, chipText: { fontSize: 12, fontWeight: '600', color: '#64748b' },
  submitBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', borderRadius: 10, padding: 14, marginTop: 8 }, submitBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 15, marginLeft: 8 },
  detailSection: { marginBottom: 16 }, detailTitle: { fontSize: 16, fontWeight: '600', color: '#1e293b', marginBottom: 12 },
  detailRow: { flexDirection: 'row', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' }, detailKey: { fontSize: 13, color: '#6B7280', flex: 1 }, detailVal: { fontSize: 13, color: '#1e293b', flex: 1, textAlign: 'right' },
});